import { useState, useEffect } from 'react';
import { api } from '@/lib/api';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

interface SystemStats {
  totalTenants: number;
  totalUsers: number;
  totalModels: number;
  totalDocuments: number;
}

interface BackupInfo {
  key: string;
  size: number;
  uploaded: string;
}

interface InviteCode {
  id: string;
  code: string;
  tenantName: string | null;
  status: 'active' | 'used' | 'expired' | 'revoked';
  usedBy: string | null;
  usedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

interface ModelInfo {
  id: string;
  legalName: string;
  stageNames: string[];
  email: string | null;
  tenantName: string;
  tenantSlug: string;
  idVerified: boolean;
  createdAt: string;
}

export default function SuperAdminConsole() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('superadmin_token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'tenants' | 'invites' | 'models' | 'backups' | 'system'>('tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // New invite code form
  const [showNewInvite, setShowNewInvite] = useState(false);
  const [newInvite, setNewInvite] = useState({
    tenantName: '',
    expiresInDays: 7,
  });
  const [createInviteLoading, setCreateInviteLoading] = useState(false);

  // New tenant form
  const [showNewTenant, setShowNewTenant] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    adminPassword: '',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (name: string) => {
    setNewTenant(prev => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : generateSlug(name),
    }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugManuallyEdited(true);
    setNewTenant(prev => ({
      ...prev,
      slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
    }));
  };

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await api.post<{ token: string; user: { role: string } }>('/auth/login', {
        email,
        password,
      });

      if (response.data.user.role !== 'super_admin') {
        setLoginError('Access denied. Super admin privileges required.');
        return;
      }

      localStorage.setItem('superadmin_token', response.data.token);
      setToken(response.data.token);
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    setToken(null);
    setTenants([]);
    setBackups([]);
    setStats(null);
  };

  const fetchTenants = async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/tenants', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch tenants');
      }

      const data = await response.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/backups', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch backups');
      }

      const data = await response.json();
      setBackups(data.backups || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newTenant),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create tenant');
      }

      setNewTenant({ name: '', slug: '', adminEmail: '', adminPassword: '' });
      setSlugManuallyEdited(false);
      setShowNewTenant(false);
      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setCreateLoading(false);
    }
  };

  const createSystemBackup = async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/backups/system', {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to create backup');
      }

      fetchBackups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  const updateTenantStatus = async (tenantId: string, status: string) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/admin/tenants/${tenantId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant');
      }

      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    }
  };

  const fetchInviteCodes = async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/invite-codes', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch invite codes');
      }

      const data = await response.json();
      setInviteCodes(data.inviteCodes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };

  const createInviteCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateInviteLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/invite-codes', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newInvite),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create invite code');
      }

      setNewInvite({ tenantName: '', expiresInDays: 7 });
      setShowNewInvite(false);
      fetchInviteCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite code');
    } finally {
      setCreateInviteLoading(false);
    }
  };

  const revokeInviteCode = async (codeId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/admin/invite-codes/${codeId}/revoke`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to revoke invite code');
      }

      fetchInviteCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invite code');
    }
  };

  const fetchModels = async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/models', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch models');
      }

      const data = await response.json();
      setModels(data.models || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const verifyModelId = async (modelId: string, verified: boolean) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/admin/models/${modelId}/verify`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ verified }),
      });

      if (!response.ok) {
        throw new Error('Failed to update model verification');
      }

      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model verification');
    }
  };

  useEffect(() => {
    if (token && activeTab === 'tenants') {
      fetchTenants();
    } else if (token && activeTab === 'backups') {
      fetchBackups();
    } else if (token && activeTab === 'invites') {
      fetchInviteCodes();
    } else if (token && activeTab === 'models') {
      fetchModels();
    }
  }, [token, activeTab]);

  // Login screen
  if (!token) {
    return (
      <div className="superadmin-login">
        <div className="superadmin-login-container">
          <div className="superadmin-login-header">
            <h1>MediaRite</h1>
            <p>Super Admin Console</p>
          </div>

          <form onSubmit={handleLogin} className="superadmin-login-form">
            {loginError && <div className="error-message">{loginError}</div>}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Super admin email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Password"
                minLength={8}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loginLoading}>
              {loginLoading ? 'Authenticating...' : 'Access Console'}
            </button>
          </form>

          <p className="superadmin-note">
            This console is protected by Cloudflare Access.
            <br />
            Only authorized super administrators may access this area.
          </p>
        </div>
      </div>
    );
  }

  // Admin console
  return (
    <div className="superadmin-console">
      <header className="superadmin-header">
        <div className="superadmin-header-left">
          <h1>MediaRite Super Admin</h1>
        </div>
        <div className="superadmin-header-right">
          <button onClick={handleLogout} className="btn btn-secondary btn-sm">
            Logout
          </button>
        </div>
      </header>

      <nav className="superadmin-nav">
        <button
          className={`superadmin-nav-btn ${activeTab === 'tenants' ? 'active' : ''}`}
          onClick={() => setActiveTab('tenants')}
        >
          Tenants
        </button>
        <button
          className={`superadmin-nav-btn ${activeTab === 'invites' ? 'active' : ''}`}
          onClick={() => setActiveTab('invites')}
        >
          Invite Codes
        </button>
        <button
          className={`superadmin-nav-btn ${activeTab === 'models' ? 'active' : ''}`}
          onClick={() => setActiveTab('models')}
        >
          Models
        </button>
        <button
          className={`superadmin-nav-btn ${activeTab === 'backups' ? 'active' : ''}`}
          onClick={() => setActiveTab('backups')}
        >
          Backups
        </button>
        <button
          className={`superadmin-nav-btn ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          System
        </button>
      </nav>

      <main className="superadmin-main">
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'tenants' && (
          <div className="superadmin-section">
            <div className="superadmin-section-header">
              <h2>Tenant Management</h2>
              <button
                onClick={() => setShowNewTenant(!showNewTenant)}
                className="btn btn-primary"
              >
                {showNewTenant ? 'Cancel' : 'New Tenant'}
              </button>
            </div>

            {showNewTenant && (
              <form onSubmit={createTenant} className="superadmin-form">
                <h3>Create New Tenant (Studio)</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Studio Name</label>
                    <input
                      type="text"
                      value={newTenant.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      required
                      placeholder="Acme Studios"
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug (URL-friendly)</label>
                    <input
                      type="text"
                      value={newTenant.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      required
                      placeholder="acme-studios"
                    />
                    <small>Auto-generated from name. Edit to customize.</small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admin Email</label>
                    <input
                      type="email"
                      value={newTenant.adminEmail}
                      onChange={(e) => setNewTenant({ ...newTenant, adminEmail: e.target.value })}
                      required
                      placeholder="admin@studio.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Password</label>
                    <input
                      type="password"
                      value={newTenant.adminPassword}
                      onChange={(e) => setNewTenant({ ...newTenant, adminPassword: e.target.value })}
                      required
                      placeholder="Min 8 characters"
                      minLength={8}
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={createLoading}>
                  {createLoading ? 'Creating...' : 'Create Tenant'}
                </button>
              </form>
            )}

            {loading ? (
              <div className="loading">Loading tenants...</div>
            ) : (
              <table className="superadmin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-state">No tenants found</td>
                    </tr>
                  ) : (
                    tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td>{tenant.name}</td>
                        <td><code>{tenant.slug}</code></td>
                        <td>
                          <span className={`status-badge status-${tenant.status}`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            {tenant.status === 'active' ? (
                              <button
                                onClick={() => updateTenantStatus(tenant.id, 'suspended')}
                                className="btn btn-danger btn-sm"
                              >
                                Suspend
                              </button>
                            ) : (
                              <button
                                onClick={() => updateTenantStatus(tenant.id, 'active')}
                                className="btn btn-success btn-sm"
                              >
                                Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'invites' && (
          <div className="superadmin-section">
            <div className="superadmin-section-header">
              <h2>Studio Invite Codes</h2>
              <button
                onClick={() => setShowNewInvite(!showNewInvite)}
                className="btn btn-primary"
              >
                {showNewInvite ? 'Cancel' : 'Generate Invite Code'}
              </button>
            </div>

            {showNewInvite && (
              <form onSubmit={createInviteCode} className="superadmin-form">
                <h3>Generate New Invite Code</h3>
                <p className="form-note">
                  Invite codes allow studios to self-register. The code will be associated with the studio name you provide.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Studio Name (optional)</label>
                    <input
                      type="text"
                      value={newInvite.tenantName}
                      onChange={(e) => setNewInvite({ ...newInvite, tenantName: e.target.value })}
                      placeholder="Pre-fill studio name"
                    />
                    <small>Leave blank to let the studio choose their name</small>
                  </div>
                  <div className="form-group">
                    <label>Expires In (days)</label>
                    <select
                      value={newInvite.expiresInDays}
                      onChange={(e) => setNewInvite({ ...newInvite, expiresInDays: Number(e.target.value) })}
                    >
                      <option value={1}>1 day</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={createInviteLoading}>
                  {createInviteLoading ? 'Generating...' : 'Generate Code'}
                </button>
              </form>
            )}

            {loading ? (
              <div className="loading">Loading invite codes...</div>
            ) : (
              <table className="superadmin-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Studio Name</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Used By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inviteCodes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">No invite codes found</td>
                    </tr>
                  ) : (
                    inviteCodes.map((invite) => (
                      <tr key={invite.id}>
                        <td><code className="invite-code">{invite.code}</code></td>
                        <td>{invite.tenantName || <span className="text-muted">Any</span>}</td>
                        <td>
                          <span className={`status-badge status-${invite.status}`}>
                            {invite.status}
                          </span>
                        </td>
                        <td>{new Date(invite.expiresAt).toLocaleDateString()}</td>
                        <td>{invite.usedBy || '-'}</td>
                        <td>
                          {invite.status === 'active' && (
                            <div className="action-buttons">
                              <button
                                onClick={() => navigator.clipboard.writeText(invite.code)}
                                className="btn btn-secondary btn-sm"
                              >
                                Copy
                              </button>
                              <button
                                onClick={() => revokeInviteCode(invite.id)}
                                className="btn btn-danger btn-sm"
                              >
                                Revoke
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'models' && (
          <div className="superadmin-section">
            <div className="superadmin-section-header">
              <h2>Model Management</h2>
              <button onClick={fetchModels} className="btn btn-secondary">
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading models...</div>
            ) : (
              <table className="superadmin-table">
                <thead>
                  <tr>
                    <th>Legal Name</th>
                    <th>Stage Names</th>
                    <th>Email</th>
                    <th>Studio</th>
                    <th>ID Verified</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-state">No models found</td>
                    </tr>
                  ) : (
                    models.map((model) => (
                      <tr key={model.id}>
                        <td>{model.legalName}</td>
                        <td>{model.stageNames?.join(', ') || '-'}</td>
                        <td>{model.email || '-'}</td>
                        <td>
                          <span className="tenant-badge">{model.tenantName}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${model.idVerified ? 'status-active' : 'status-pending'}`}>
                            {model.idVerified ? 'Verified' : 'Unverified'}
                          </span>
                        </td>
                        <td>{new Date(model.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            {model.idVerified ? (
                              <button
                                onClick={() => verifyModelId(model.id, false)}
                                className="btn btn-warning btn-sm"
                              >
                                Unverify
                              </button>
                            ) : (
                              <button
                                onClick={() => verifyModelId(model.id, true)}
                                className="btn btn-success btn-sm"
                              >
                                Verify ID
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'backups' && (
          <div className="superadmin-section">
            <div className="superadmin-section-header">
              <h2>System Backups</h2>
              <button onClick={createSystemBackup} className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create System Backup'}
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading backups...</div>
            ) : (
              <table className="superadmin-table">
                <thead>
                  <tr>
                    <th>Backup File</th>
                    <th>Size</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="empty-state">No backups found</td>
                    </tr>
                  ) : (
                    backups.map((backup) => (
                      <tr key={backup.key}>
                        <td><code>{backup.key}</code></td>
                        <td>{(backup.size / 1024).toFixed(2)} KB</td>
                        <td>{new Date(backup.uploaded).toLocaleString()}</td>
                        <td>
                          <button className="btn btn-secondary btn-sm">
                            Download
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'system' && (
          <div className="superadmin-section">
            <h2>System Information</h2>

            <div className="system-info-grid">
              <div className="system-info-card">
                <h3>Environment</h3>
                <p>Development</p>
              </div>
              <div className="system-info-card">
                <h3>API Status</h3>
                <p className="status-healthy">Healthy</p>
              </div>
              <div className="system-info-card">
                <h3>Database</h3>
                <p>Cloudflare D1</p>
              </div>
              <div className="system-info-card">
                <h3>Storage</h3>
                <p>Cloudflare R2</p>
              </div>
            </div>

            <div className="system-actions">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button onClick={fetchTenants} className="btn btn-secondary">
                  Refresh Data
                </button>
                <button onClick={createSystemBackup} className="btn btn-secondary">
                  Create Backup
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
